import { useCallback, useRef } from 'react';
import { Platform, type SectionListScrollParams } from 'react-native';

import { useAnchorSectionList } from './context';
import type { FlashListData } from './types';

const EMPTY_SECTION_HEADER_ATTR = 'data-fhs-empty-section-header';
const SECTION_CONTENT_ATTR = 'data-fhs-section-content';
const SECTION_HEADER_ATTR = 'data-fhs-section-header';

const FOCUSABLE_SELECTOR =
  'a[href]:not([aria-disabled="true"]), button:not([disabled]):not([aria-disabled="true"]), [role="link"]:not([aria-disabled="true"])';

function getScrollIndexForSection(flashListData: FlashListData, sectionId: string): number {
  const sectionIndex = flashListData.findIndex(
    datum => datum.type === 'section' && datum.section.id === sectionId
  );

  if (sectionIndex < 0) {
    return -1;
  }

  const sectionDatum = flashListData[sectionIndex];
  if (sectionDatum.type === 'section' && !sectionDatum.section.title.trim()) {
    const customIndex = flashListData.findIndex(
      (datum, index) =>
        index > sectionIndex && datum.type === 'custom' && datum.section.id === sectionId
    );
    if (customIndex >= 0) {
      return customIndex;
    }
  }

  return sectionIndex;
}

function getSectionHeaderElements(): HTMLElement[] {
  return Array.from(
    document.querySelectorAll<HTMLElement>(
      `[${SECTION_HEADER_ATTR}], [${EMPTY_SECTION_HEADER_ATTR}="true"]`
    )
  );
}

/**
 * First menu item link after this section header and before the next section, excluding
 * the category sidebar (tablist).
 */
function findFirstMenuItemInSection(sectionEl: HTMLElement): HTMLElement | null {
  const sectionHeaders = getSectionHeaderElements();
  const headerIndex = sectionHeaders.findIndex(
    header => header === sectionEl || header.id === sectionEl.id
  );
  const nextSectionHeader = headerIndex >= 0 ? sectionHeaders[headerIndex + 1] : undefined;

  const candidates = Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

  return (
    candidates.find(candidate => {
      const isAfterSection = !!(
        sectionEl.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING
      );
      const isBeforeNextSection =
        !nextSectionHeader ||
        !!(
          candidate.compareDocumentPosition(nextSectionHeader) &
          Node.DOCUMENT_POSITION_PRECEDING
        );
      const isInSidebar = !!candidate.closest('[role="tablist"]');

      return isAfterSection && isBeforeNextSection && !isInSidebar;
    }) ?? null
  );
}

/** Focus the first actionable item in the section (menu tile), not only the heading. */
function getSectionFocusTarget(sectionId: string, sectionEl: HTMLElement): HTMLElement | null {
  if (sectionEl.getAttribute(EMPTY_SECTION_HEADER_ATTR) === 'true') {
    const sectionContent = document.querySelector<HTMLElement>(
      `[${SECTION_CONTENT_ATTR}="${sectionId}"]`
    );
    const focusable = sectionContent?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);

    return focusable ?? null;
  }

  const firstMenuItem = findFirstMenuItemInSection(sectionEl);
  if (firstMenuItem) {
    return firstMenuItem;
  }

  // Section header is in the DOM but FlashList items may not be yet — keep polling.
  return null;
}

export function useScrollToSection() {
  const context = useAnchorSectionList();
  const intervalIdRef = useRef<ReturnType<typeof setInterval>>();
  return useCallback(
    (
      sectionId: string,
      opts?: Pick<SectionListScrollParams, 'animated' | 'viewOffset' | 'viewPosition'>
    ) => {
      const scrollIndex = getScrollIndexForSection(context.flashListData, sectionId);

      if (scrollIndex < 0) {
        return;
      }

      context.flashListRef.current?.scrollToIndex({ ...opts, index: scrollIndex });

      // Handle focus for web
      if (Platform.OS === 'web') {
        clearInterval(intervalIdRef.current);

        let pollingDuration = 0;
        const INTERVAL_DURATION = 100;
        const MAX_TIME_BEFORE_GIVE_UP = 5000;

        // We have to check on an interval because sections are virtualized
        // the section that we want to focus might not exist in the dom.
        // we should be actively scrolling towards the section so it should
        // eventually be rendered into dom.
        intervalIdRef.current = setInterval(() => {
          pollingDuration += INTERVAL_DURATION;

          if (pollingDuration > MAX_TIME_BEFORE_GIVE_UP) {
            clearInterval(intervalIdRef.current);
            return;
          }

          const sectionEl = document.getElementById(sectionId);
          if (!sectionEl) {
            // haven't found section id, wait until next interval to try again.
            return;
          }

          const focusTarget = getSectionFocusTarget(sectionId, sectionEl);
          if (!focusTarget) {
            return;
          }

          focusTarget.focus({ preventScroll: true });
          clearInterval(intervalIdRef.current);
        }, INTERVAL_DURATION);
      }
    },
    [context.flashListData, context.flashListRef]
  );
}
